import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import AddModel from "../../../components/Add/AddModel";
import AddInputs from "../../../components/Add/AddInputs";
import AddBottomButton from "../../../components/Add/AddBottomButton";
import CheckboxMap from "../../../components/checkbox/CheckboxMap";
import CurrencyInputField from "../../../components/inputfields/InputFieldWithCurrency";
import TextArea from "../../../components/inputfields/TextArea";
import InputLabel from "../../../components/inputfields/InputLabel";
import ErrorMessage from "../../../components/ErrorMessages/ErrorMessage";
import useApi from "../../../services/useApi";
import endpoints from "../../../services/endpoints";
import validateMandatoryFields from "../../../components/commonfunctions/validateMandatoryFields";
import { showToast } from "../../../components/toastMessage/toastSlice";
import { RootState } from "../../../store";
import { bundlePlanForm, bundlePlanPayload } from "../components/types";
import {
  bundlePlanInitialState,
  getBundlePlanInputs,
  MAX_PLAN_AMOUNT,
  PLAN_NAME_MAX_LENGTH,
  PLAN_TYPE_OPTIONS,
} from "./bundlePlanFields";

export default function AddBundlePlan({ edit }: { edit?: boolean }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, planId } = useParams();
  const { bundlePlans } = endpoints;
  const { getAPI, PostAPI, PatchAPI } = useApi();
  const pathname = useSelector((state: RootState) => state?.geolocation?.previousUrl);

  const [state, setState] = useState<bundlePlanForm>(bundlePlanInitialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [planTypes, setPlanTypes] = useState<string[]>([]);
  const [planTypeError, setPlanTypeError] = useState("");
  const [planNameError, setPlanNameError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const listingUrl = `/mno-management/list-of-mnos/view-mno/${id}/bundle-plans`;

  const handleInputChange = (fieldId: string, value: string) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldId]: "",
    }));
    setState((prevState) => ({
      ...prevState,
      [fieldId]: value,
    }));
  };

  const handlePlanType = (option: string) => {
    setPlanTypeError("");
    setPlanTypes((prev) =>
      prev.includes(option) ? prev.filter((item) => item !== option) : [...prev, option]
    );
  };

  const handlePlanName = (value: string) => {
    setPlanNameError("");
    setState((prevState) => ({
      ...prevState,
      plan_name: value,
    }));
  };

  /**
   * Validates every mandatory field along with the plan type selection, the
   * amount ceiling and the plan name length before building the payload.
   */
  const validate = () => {
    const mandatoryFields = [
      "bundle_id",
      "amount_excl_tax",
      "amount_incl_tax",
      "internal_subscription_fee",
    ];
    const errorObject = validateMandatoryFields(state, mandatoryFields);
    let isValid = Object.keys(errorObject).length === 0;

    if (!state.plan_name.trim()) {
      setPlanNameError("This field is required");
      isValid = false;
    } else if (state.plan_name.length > PLAN_NAME_MAX_LENGTH) {
      setPlanNameError(`Plan name cannot exceed ${PLAN_NAME_MAX_LENGTH} characters`);
      isValid = false;
    }

    if (planTypes.length === 0) {
      setPlanTypeError("Please select at least one plan type");
      isValid = false;
    }

    // The bundle ID is alphanumeric only. Surrounding whitespace is trimmed before
    // the check so a stray space is not reported as an invalid character.
    if (state.bundle_id.trim() && !/^[a-zA-Z0-9]+$/.test(state.bundle_id.trim())) {
      errorObject.bundle_id = "Bundle ID should contain only letters and numbers";
      isValid = false;
    }

    (["amount_excl_tax", "amount_incl_tax"] as const).forEach((field) => {
      if (errorObject[field]) return;
      const amount = Number(state[field]);
      if (amount <= 0) {
        errorObject[field] = "Amount should be greater than 0";
        isValid = false;
      } else if (amount > MAX_PLAN_AMOUNT) {
        errorObject[field] = `Amount cannot exceed AFA ${MAX_PLAN_AMOUNT}`;
        isValid = false;
      }
    });

    // The tax inclusive amount can never be lower than the exclusive one.
    if (
      !errorObject.amount_excl_tax &&
      !errorObject.amount_incl_tax &&
      Number(state.amount_incl_tax) < Number(state.amount_excl_tax)
    ) {
      errorObject.amount_incl_tax =
        "Amount including tax must be greater than or equal to amount excluding tax";
      isValid = false;
    }

    if (!errorObject.internal_subscription_fee && Number(state.internal_subscription_fee) < 0) {
      errorObject.internal_subscription_fee = "Internal subscription fee cannot be negative";
      isValid = false;
    } else if (
      !errorObject.internal_subscription_fee &&
      Number(state.internal_subscription_fee) > MAX_PLAN_AMOUNT
    ) {
      errorObject.internal_subscription_fee = `Internal subscription fee cannot exceed AFA ${MAX_PLAN_AMOUNT}`;
      isValid = false;
    }

    setErrors(errorObject);
    return isValid;
  };

  const handleSubmit = async () => {
    setErrors({});
    setPlanTypeError("");
    setPlanNameError("");
    if (!validate()) return;

    const payload: bundlePlanPayload = {
      mno_id: id || "",
      bundle_id: state.bundle_id.trim(),
      amount_excl_tax: Number(state.amount_excl_tax),
      amount_incl_tax: Number(state.amount_incl_tax),
      internal_subscription_fee: Number(state.internal_subscription_fee),
      plan_name: state.plan_name.trim(),
      // Keep the selection in the canonical Voice, Data, SMS order.
      plan_types: PLAN_TYPE_OPTIONS.filter((option) => planTypes.includes(option)),
    };

    setLoading(true);
    const response = edit
      ? await PatchAPI(`${bundlePlans}/${planId}`, payload)
      : await PostAPI(bundlePlans, payload);

    if (!response?.error) {
      dispatch(
        showToast({
          message:
            response?.data?.message ||
            (edit ? "Bundle plan updated successfully" : "Bundle plan created successfully"),
          type: "success",
        })
      );
      navigate(listingUrl);
    } else {
      dispatch(
        showToast({
          message: response?.data?.response?.data?.message || "Something went wrong.",
          type: "error",
        })
      );
    }
    setLoading(false);
  };

  const getPlanDetails = async () => {
    setEditLoading(true);
    const response = await getAPI(`${bundlePlans}/${planId}`);
    if (!response?.error) {
      const data = response?.data?.data;
      setState({
        bundle_id: data?.bundle_id?.toString() || "",
        amount_excl_tax: data?.amount_excl_tax?.toString() || "",
        amount_incl_tax: data?.amount_incl_tax?.toString() || "",
        internal_subscription_fee: data?.internal_subscription_fee?.toString() || "",
        plan_name: data?.plan_name || "",
      });
      setPlanTypes(Array.isArray(data?.plan_types) ? data.plan_types : []);
    } else {
      navigate("/not-found", { state: { route: listingUrl } });
    }
    setEditLoading(false);
  };

  useEffect(() => {
    if (edit && planId) {
      getPlanDetails();
    }
  }, []);

  return (
    <div>
      <Breadcrumbs
        navPath={[
          "MNO Management",
          "List of MNO’s",
          "View Details",
          "Bundle Plan List",
          `${edit ? "Edit" : "Create"} Plan`,
        ]}
        heading={`${edit ? "Edit" : "Create"} Bundle Plan`}
        noNavigation
        fullNavPath
        // One link per crumb so the indices stay aligned; the last crumb is the
        // current page and is never clickable.
        links={[
          pathname ? pathname : "/mno-management/list-of-mnos",
          pathname ? pathname : "/mno-management/list-of-mnos",
          `/mno-management/list-of-mnos/view-mno/${id}`,
          listingUrl,
          "",
        ]}
        hasAddPrivilege={true}
      />
      <AddModel>
        <h1 className="text-heading3 text-primary_text py-4">Bundle Plan Details</h1>
        <AddInputs
          inputs={getBundlePlanInputs()}
          totalInputCount={5}
          inputValues={state}
          onInputChange={handleInputChange}
          mapping={true}
          errors={errors}
          editLoading={editLoading}
        />
        {/* Plan Type and the subscription fee share a row, matching the column
            widths that AddInputs lays out for the fields above. */}
        <div className="-mx-5 flex flex-wrap w-full">
          <div className="px-5 w-full sm:w-1/2 lg:w-1/3 mb-5">
            <InputLabel labelName="Plan Type" id="plan_type" required={true} />
            <CheckboxMap
              options={PLAN_TYPE_OPTIONS}
              checkedArray={planTypes}
              handelCheckBox={handlePlanType}
              error={planTypeError}
              className="gap-x-6"
            />
            {planTypeError && <ErrorMessage errorMessage={planTypeError} />}
          </div>
          {/* Rendered directly rather than through AddInputs: in mapping mode
              AddInputs emits its own full-width flex container, which would push
              this field onto a line of its own instead of beside Plan Type. */}
          <div className="px-5 w-full sm:w-1/2 lg:w-1/3 mb-5">
            <CurrencyInputField
              inputItem={{
                labelName: "Internal Subscription Fee",
                id: "internal_subscription_fee",
              }}
              value={state.internal_subscription_fee}
              onChange={(event) =>
                handleInputChange("internal_subscription_fee", event.target.value)
              }
              errorMessage={errors.internal_subscription_fee}
              required={true}
              tabIndex={0}
              min={0}
              max={MAX_PLAN_AMOUNT}
              editLoading={editLoading}
              // Without customWidth the field falls back to a fixed w-80, which is
              // narrower than the amount fields above it.
              customWidth="w-full"
              testId="internal_subscription_fee"
            />
          </div>
        </div>
        <div className="w-full sm:pr-6">
          <TextArea
            id="plan_name"
            label="Plan Name"
            text={state.plan_name}
            setText={(value) => handlePlanName(value as string)}
            textError={planNameError}
            setTextError={setPlanNameError}
            maxLength={PLAN_NAME_MAX_LENGTH}
            placeholderRequied={false}
            required={true}
          />
        </div>
        <div className="w-full sm:pr-6 mt-2">
          <p className="bg-[#E5F0FC] text-primary_text text-body2 rounded-lg px-4 py-3">
            Note: The USSD Plan Code will be generated automatically once the new plan is saved
          </p>
        </div>
      </AddModel>
      <AddBottomButton
        buttonWidth={"w-[140px]"}
        buttonName={edit ? "Update Plan" : "Save Plan"}
        buttonClass={"w-[140px] whitespace-nowrap"}
        onClick={handleSubmit}
        disabled={false}
        loading={loading}
        secondaryButtonName="Cancel"
        secondaryButtonClass="w-[120px] h-[45px] font-semibold text-small"
        secondaryButtonFunction={() => {
          navigate(listingUrl);
        }}
        align="end"
        testID="save_bundle_plan"
      />
    </div>
  );
}
